import type MiniSearch from "minisearch";
import { useEffect, useRef, useState } from "react";

import type { SearchIndexEntry, SearchResult } from "@/lib/search";
import {
  loadSearchIndex,
  refreshSearchIndexIfStale,
  searchArchive,
} from "@/lib/search";

interface ArchiveSearch {
  /** True while the index is being fetched for the first time. */
  loading: boolean;
  /** Call when the search input is focused, to load the index lazily. */
  prepare: () => void;
  ready: boolean;
  results: SearchResult[];
}

/**
 * Search over the whole archive, indexed in the browser.
 *
 * The index is loaded on first focus rather than on mount, so a visitor who
 * never searches never pays for it. Once loaded it is shared across every
 * component via the module-level cache in `lib/search`.
 */
export const useArchiveSearch = (query: string): ArchiveSearch => {
  const [index, setIndex] = useState<MiniSearch<SearchIndexEntry> | null>(null);
  const [loading, setLoading] = useState(false);
  const requested = useRef(false);

  const prepare = () => {
    if (requested.current) {
      return;
    }
    requested.current = true;
    setLoading(true);

    const load = async () => {
      try {
        const loaded = await loadSearchIndex();
        setIndex(loaded?.index ?? null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  };

  // Typing implies intent even if focus arrived some other way (a shortcut,
  // a restored form value), so make sure the index is on its way.
  useEffect(() => {
    if (query.trim()) {
      prepare();
    }
    // `prepare` guards itself against repeat calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Content can change while a tab is open. Re-check the fingerprint once the
  // index is in use rather than trusting it for the lifetime of the session.
  useEffect(() => {
    if (!index) {
      return;
    }
    let active = true;
    const check = async () => {
      const latest = await refreshSearchIndexIfStale();
      if (active && latest && latest.index !== index) {
        setIndex(latest.index);
      }
    };
    void check();
    return () => {
      active = false;
    };
  }, [index]);

  const results = index ? searchArchive(index, query) : [];

  return { loading, prepare, ready: Boolean(index), results };
};
